import { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";

const ImagesAttachments = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { errors, setErrors, setImages, images: uploadedImages } = usePortServicesFlow();

  const imagesUploadSignatureRef = useRef<string | null>(null);
  const uploadedImagesRef = useRef(uploadedImages);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const images = useUploadStore((s) => s.filesByKey.images ?? EMPTY_UPLOAD_ENTRIES);

  useEffect(() => {
    uploadedImagesRef.current = uploadedImages;
  }, [uploadedImages]);

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.images;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setImagesFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("images");
        return;
      }
      setFilesForKey("images", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: { current: string | null },
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeImages = useCallback(() => {
    clearFilesForKey("images");
    setImages([]);
  }, [clearFilesForKey, setImages]);

  const getEntryKey = (entry: UploadEntry, idx: number) => {
    if (entry instanceof File) {
      return `file-${entry.name}-${entry.lastModified}-${entry.size}`;
    }
    if (entry && typeof entry === "object") {
      const name = entry.name;
      const rawUrl = "url" in entry ? (entry as { url?: unknown }).url : undefined;
      const rawIdx = "idx" in entry ? (entry as { idx?: unknown }).idx : undefined;
      const url = typeof rawUrl === "string" ? rawUrl : "";
      const entryIdx =
        typeof rawIdx === "number" || typeof rawIdx === "string" ? String(rawIdx) : "";
      return `obj-${String(name)}-${String(url)}-${String(entryIdx)}-${idx}`;
    }
    return `unknown-${idx}`;
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          const nextImages = [
            ...uploadedImagesRef.current,
            {
              documentUrl,
              documentName: file.name,
              fileType: file.type,
            },
          ];
          uploadedImagesRef.current = nextImages;
          setImages(nextImages);
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: { images?: string; files?: string }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: { current: string | null };
  }) => {
    if (!newFiles.length) {
      return;
    }

    const existingFileSignatures = new Set(
      images
        .map((entry) => getFileSignature(entry))
        .filter((signature): signature is string => !!signature),
    );
    const filesToUpload = newFiles.filter(
      (file) => !existingFileSignatures.has(getFileSignature(file) ?? ""),
    );

    setUploadErrors({ files: undefined });
    setUploadFiles(newFiles);
    filesToUpload.forEach((file) => {
      triggerUpload(file, onUploadDocument, uploadTracker);
    });
  };

  useEffect(() => {
    return () => {
      imagesUploadSignatureRef.current = null;
      removeImages();
    };
  }, [removeImages]);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-images">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.images`)}
      </Typography>
      <DocumentUpload
        testId="port-services-images-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
                images: fileErrors.images,
              }),
            setUploadFiles: setImagesFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "IMAGES"),
            uploadTracker: imagesUploadSignatureRef,
          })
        }
        name="images"
        conditions={internalProfile}
        files={images}
        setFiles={(next) => setFilesForKey("images", next)}
        maxFiles={25}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.images-only")}
      />
      {errors.images && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.images}</FormHelperText>
      )}
      {images.map((file, idx) => (
        <SingleFile
          key={getEntryKey(file, idx)}
          file={file}
          testId={`port-services-images-${idx}`}
          fieldName="images"
          currentFileIndex={idx}
          setFiles={() => {
            setErrors({ ...errors, files: undefined });
            setImagesFiles(images.filter((_, i) => i !== idx));
            setImages(uploadedImages.filter((_, i) => i !== idx));
            imagesUploadSignatureRef.current = null;
          }}
          removeFn={() => {
            setImagesFiles(images.filter((_, i) => i !== idx));
            setImages(uploadedImages.filter((_, i) => i !== idx));
          }}
        />
      ))}
    </div>
  );
};

export default ImagesAttachments;
